import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { FlaskConical } from 'lucide-react';
import api from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { formatApiError } from '@/lib/errors';

const LIVE_ORIGIN = 'https://mamv.info';
const TEST_ORIGIN = 'https://mamv-test.188.245.76.105.sslip.io';

// Verwaltung und Sekretariat, Buchhaltung, Admins. Alte Rollennamen bleiben gültig.
const SWITCH_ROLES = new Set([
  'verwaltung', 'sekretariat', 'secretary', 'management',
  'buchhaltung', 'accounting', 'kasse',
  'admin', 'super_admin',
]);

export function canSwitchEnvironment(user, isSuperAdmin) {
  if (isSuperAdmin || user?.is_super_admin) return true;
  return (user?.roles || []).some((role) => SWITCH_ROLES.has(String(role?.name || '').toLowerCase()));
}

function samePage(origin) {
  return `${origin}${window.location.pathname}${window.location.search}`;
}

/**
 * Testumgebung: alle sehen den Hinweis. Wechseln dürfen Verwaltung, Buchhaltung,
 * Admins und S-Admins. „Ok zum Deploy“ nur für S-Admins, nach einer zweiten Bestätigung.
 * Auf der Live-Seite gibt es für dieselben Rollen den Weg in die Testversion.
 */
export function StagingBanner() {
  const { user } = useAuth();
  const { isSuperAdmin } = usePermissions();
  const [channel, setChannel] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const canSwitch = canSwitchEnvironment(user, isSuperAdmin);
  const barRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/health')
      .then((response) => response.json())
      .then((data) => {
        if (!cancelled) setChannel(data?.channel || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const node = barRef.current;
    const root = document.documentElement;
    if (!node) {
      root.style.removeProperty('--test-bar');
      delete root.dataset.appChannel;
      return undefined;
    }
    const apply = () => {
      root.dataset.appChannel = 'marked';
      root.style.setProperty('--test-bar', `${node.offsetHeight}px`);
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--test-bar');
      delete root.dataset.appChannel;
    };
  }, [channel, canSwitch, confirming]);

  if (channel === 'production') {
    if (!canSwitch) return null;
    return (
      <div
        ref={barRef}
        className="sticky top-0 z-40 shrink-0 flex flex-wrap items-center gap-3 px-6 py-2 bg-slate-50 border-b border-slate-200 text-sm text-slate-800"
        data-testid="live-banner"
      >
        <span>Live-Seite.</span>
        <Button size="sm" variant="outline" className="ml-auto" asChild>
          <a href={samePage(TEST_ORIGIN)} data-testid="live-test-link">Zur Testversion</a>
        </Button>
      </div>
    );
  }

  if (channel !== 'staging') return null;

  const confirm = async () => {
    setBusy(true);
    try {
      const response = await api.post('/deploy/promote');
      if (response.data?.already_live) {
        toast.success('Die Live-Seite hat diesen Stand schon.');
      } else {
        toast.success('Freigegeben. Die Live-Seite übernimmt diesen Stand.');
      }
      setConfirming(false);
    } catch (error) {
      toast.error(formatApiError(error, 'Freigabe fehlgeschlagen'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={barRef}
      className="sticky top-0 z-50 shrink-0 flex flex-wrap items-center gap-3 px-6 py-2.5 bg-red-600 text-white text-sm"
      data-testid="staging-banner"
      role="status"
    >
      <FlaskConical className="w-5 h-5 shrink-0" />
      <span className="text-base font-bold tracking-wide">Testumgebung</span>
      <span className="font-normal text-red-100">
        Neue Funktionen sind hier noch nicht auf der Live-Seite.
      </span>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {canSwitch && (
          <Button size="sm" variant="outline" className="bg-white text-red-700 border-white hover:bg-red-50" asChild>
            <a href={samePage(LIVE_ORIGIN)} data-testid="staging-live-link">Zur Liveversion</a>
          </Button>
        )}
        {isSuperAdmin && !confirming && (
          <Button
            size="sm"
            className="bg-white text-red-700 hover:bg-red-50"
            onClick={() => setConfirming(true)}
            data-testid="staging-deploy-button"
          >
            Ok zum Deploy
          </Button>
        )}
        {isSuperAdmin && confirming && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-normal text-red-100">
              Alles, was hier neuer ist als die Live-Seite, wird danach ausgeliefert.
            </span>
            <Button
              size="sm"
              variant="outline"
              className="bg-white text-red-700 border-white hover:bg-red-50"
              onClick={() => setConfirming(false)}
              disabled={busy}
              data-testid="staging-deploy-cancel"
            >
              Abbrechen
            </Button>
            <Button
              size="sm"
              className="bg-white text-red-700 hover:bg-red-50"
              onClick={confirm}
              disabled={busy}
              data-testid="staging-deploy-confirm"
            >
              Ok zum Deploy
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
